import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { colors, spacing, Text } from '@retailer-platform/shared-components'
import {
  MerchandisingFormSection,
  type Props as MerchandisingFormSectionProps,
} from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { useDashMessages } from 'intl/intl.hooks'

interface Props extends Pick<MerchandisingFormSectionProps, 'titleSuffix'> {
  smallUrl: string
  largeUrl: string
  imageAltText: string
}

const sectionContainer = css({
  display: 'flex',
  flexDirection: 'column',
})

const rowContainer = css({
  display: 'flex',
  marginBottom: spacing.X24,
  width: '100%',
})

const imageLargeContainer = css({
  width: '100%',
})

const imageSmallContainer = css({
  width: '280px',
})

const placeholderImageStyle = css({
  border: `1px dashed ${colors.GRAYSCALE.X30}`,
  marginTop: spacing.X8,
  marginLeft: spacing.X8,
  marginRight: spacing.X8,
})

const imageStyle = css({
  width: '100%',
  display: 'block',
})

export const HeroBannerDetailsContentImage: FunctionComponent<Props> = ({
  titleSuffix,
  largeUrl,
  smallUrl,
  imageAltText,
}) => {
  const messages = useDashMessages({
    largeImageLabel: 'merchandising.hero-banners.section.content.field.imageLarge.label',
    smallImageLabel: 'merchandising.hero-banners.section.content.field.imageSmall.label',
    altTextLabel: 'merchandising.hero-banners.section.content.field.altText.label',
    emptyValue: 'merchandising.hero-banners.field.empty.value',
  })

  return (
    <MerchandisingFormSection
      titleId="merchandising.hero-banners.section.content.title"
      titleSuffix={titleSuffix}
    >
      <div css={sectionContainer}>
        <div css={rowContainer} key="herobanner-details-info-image-large">
          <div css={[imageLargeContainer]}>
            <Text weight="semibold">{messages.largeImageLabel}</Text>
            <div css={placeholderImageStyle}>
              <img css={imageStyle} src={largeUrl} />
            </div>
          </div>
        </div>
        <div css={rowContainer} key="herobanner-details-info-image-small">
          <div css={imageSmallContainer}>
            <Text weight="semibold">{messages.smallImageLabel}</Text>
            <div css={placeholderImageStyle}>
              <img css={imageStyle} src={smallUrl} />
            </div>
          </div>
        </div>
        <div css={rowContainer} key="herobanner-details-info-alttext">
          <div css={imageSmallContainer}>
            <Text weight="semibold">{messages.altTextLabel}</Text>
            <Text size="medium">{imageAltText || messages.emptyValue}</Text>
          </div>
        </div>
      </div>
    </MerchandisingFormSection>
  )
}
